import { apiClient } from './api';

// 分享状态
export type ShareStatus = 'active' | 'expired' | 'revoked' | 'depleted';

// 分享信息
export interface ShareInfo {
  id: string;
  shareToken: string;
  workflowName: string;
  status: ShareStatus;
  requirePassword: boolean;
  expireAt?: string;
  createdAt: string;
  // 来源信息
  sourceType?: string; // workflow: 工作流, template: 模板
  versionNum?: number; // 工作流版本号（模板为 0）
}

// 工作流定义
export interface WorkflowNode {
  id: string;
  type: string;
  label: string;
  position?: { x: number; y: number };
  config?: Record<string, unknown>;
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
}

export interface WorkflowDefinition {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

// 创建分享请求
export interface CreateShareRequest {
  workflowExecutionId: string;
  password?: string; // 不传则自动生成
  expireAt?: string; // ISO 8601 格式
  maxViewCount?: number; // 0 表示无限制
  dataLimit?: number; // -1 表示所有，默认 10
}

// 创建分享响应
export interface CreateShareResponse {
  share: {
    id: string;
    shareToken: string;
    userId: string;
    workflowExecutionId: string;
    workflowId: string;
    workflowName: string;
    password: string;
    expireAt?: string;
    maxViewCount: number;
    viewCount: number;
    dataLimit: number;
    status: ShareStatus;
    copyCount: number;
    createdAt: string;
    updatedAt: string;
  };
  shareUrl: string;
}

// 分组结果
export interface ShareGroupedResult {
  group_key: string;
  columns: string[];
  rows: Record<string, unknown>[];
  rowCount: number;
}

// 分享数据响应
export interface ShareDataResponse {
  shareInfo: ShareInfo;
  workflow: WorkflowDefinition;
  data: Record<string, unknown>[];
  columns: string[];
  totalRows: number;
  canCopy: boolean;
  // 扩展字段，支持 diff 和分组显示
  metadata?: Record<string, unknown>;
  is_grouped?: boolean;
  grouped_results?: ShareGroupedResult[];
}

// 我的分享列表项
export interface MyShareItem {
  id: string;
  shareToken: string;
  workflowExecutionId: string;
  workflowId: string;
  workflowName: string;
  password: string;
  expireAt?: string;
  maxViewCount: number;
  viewCount: number;
  dataLimit: number;
  status: ShareStatus;
  copyCount: number;
  createdAt: string;
  updatedAt: string;
}

// 分享统计
export interface ShareStatistics {
  totalShares: number;
  activeShares: number;
  totalViews: number;
  totalCopies: number;
}

// 复制工作流请求
export interface CopyWorkflowRequest {
  newName?: string;
}

// 复制工作流响应
export interface CopyWorkflowResponse {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
}

// 验证密码请求
export interface VerifyPasswordRequest {
  password: string;
}

// 创建 axios 实例用于公开分享接口（可选认证）
const shareApiClient = apiClient;

// 分享 API
export const shareApi = {
  // 创建分享（需要认证）
  createShare: (data: CreateShareRequest) =>
    shareApiClient.post<unknown, { code: number; message: string; data: CreateShareResponse }>('/shares', data),

  // 获取我的分享列表（需要认证）
  listMyShares: (params?: { page?: number; pageSize?: number; workflowId?: string }) =>
    shareApiClient.get<unknown, { code: number; message: string; data: { total: number; items: MyShareItem[] } }>('/shares', { params }),

  // 获取分享详情（需要认证，仅限分享者）
  getShareDetail: (id: string) =>
    shareApiClient.get<unknown, { code: number; message: string; data: MyShareItem }>(`/shares/${id}`),

  // 撤销分享（需要认证）
  revokeShare: (id: string) =>
    shareApiClient.post<unknown, { code: number; message: string; data: { message: string } }>(`/shares/${id}/revoke`),

  // 删除分享（需要认证）
  deleteShare: (id: string) =>
    shareApiClient.delete<unknown, { code: number; message: string; data: { message: string } }>(`/shares/${id}`),

  // 获取分享统计（需要认证）
  getShareStatistics: () =>
    shareApiClient.get<unknown, { code: number; message: string; data: ShareStatistics }>('/shares/statistics'),

  // ── 公开接口 ──
  // 注意：使用单数 /share 路径避免与 /shares/:id 冲突

  // 获取分享信息（公开，无需认证）
  getShareInfo: (token: string) =>
    shareApiClient.get<unknown, { code: number; message: string; data: ShareInfo }>(`/share/${token}/info`),

  // 验证分享密码（公开）
  verifyPassword: (token: string, password: string) =>
    shareApiClient.post<unknown, { code: number; message: string; data: { verified: boolean } }>(`/share/${token}/verify`, { password }),

  // 获取分享数据（公开，可选认证）
  getShareData: (token: string) =>
    shareApiClient.get<unknown, { code: number; message: string; data: ShareDataResponse }>(`/share/${token}/data`),

  // 复制工作流（需要认证）
  copyWorkflow: (token: string, data?: CopyWorkflowRequest) =>
    shareApiClient.post<unknown, { code: number; message: string; data: CopyWorkflowResponse }>(`/share/${token}/copy`, data),
};

// 构建完整分享链接（前端页面路由）
export function buildShareUrl(token: string): string {
  const baseUrl = window.location.origin;
  return `${baseUrl}/share/${token}`;
}

// 生成随机密码
export function generateRandomPassword(): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let password = '';
  for (let i = 0; i < 6; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
}

// 格式化过期时间选项
export const expireOptions = [
  { value: 1, label: '1小时', unit: 'hour' },
  { value: 24, label: '1天', unit: 'day' },
  { value: 168, label: '7天', unit: 'week' },
  { value: 720, label: '30天', unit: 'month' },
  { value: 0, label: '永不过期', unit: 'never' },
];

// 格式化数据限制选项
export const dataLimitOptions = [
  { value: 10, label: '10条' },
  { value: 50, label: '50条' },
  { value: 100, label: '100条' },
  { value: -1, label: '所有数据' },
];
